import { useEffect, useRef, useCallback, useState } from 'react';
import { serverNow } from '../../utils/serverTime';

export interface TestTimerProps {
  timeLimit: number; // 秒
  isActive: boolean;
  onTimeout: () => void;
  /**
   * 回答締切時刻（epoch ms、サーバー時刻基準の scheduled_close_time）。
   * 指定時は毎 tick「deadlineMs − serverNow()」の差分で残り時間を計算する（管理画面と同方式）。
   * 開始時刻アンカー起点の減算と異なり、端数の切り捨て誤差が発生しない。
   */
  deadlineMs?: number;
  remainingTime?: number;
  onTick?: (remainingTime: number) => void;
}

/**
 * Format seconds to MM:SS display format.
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * TestTimer - Countdown timer component for test questions.
 * 滑らかなカウントダウンを実現するため、100msごとに更新します。
 *
 * Validates: Requirements 6.2, 6.4
 */
export function TestTimer({
  timeLimit,
  isActive,
  onTimeout,
  deadlineMs,
  remainingTime: externalRemainingTime,
  onTick,
}: TestTimerProps) {
  const intervalRef = useRef<number | null>(null);
  const internalTimeRef = useRef(timeLimit);
  const startTimeRef = useRef<number | null>(null);
  const [displayTime, setDisplayTime] = useState(timeLimit);
  const initialExternalTimeRef = useRef<number | undefined>(undefined);
  const timeoutFiredRef = useRef(false);
  const previousTimeLimitRef = useRef<number | null>(null);
  const previousIsActiveRef = useRef<boolean>(false);
  const previousDeadlineMsRef = useRef<number | undefined>(undefined);

  // コールバックを ref に保持し、effect の依存から外すことで無限ループを防ぐ
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // タイマーの開始を管理（isActiveがtrueになったときのみ開始、一度開始したら停止しない）
  // externalRemainingTimeが変わった場合も再開始する
  const [timerStartedKey, setTimerStartedKey] = useState(0);
  
  useEffect(() => {
    // timeLimitが変わった、またはisActiveがfalseからtrueに変わったときにタイマーをリセット
    const timeLimitChanged = previousTimeLimitRef.current !== null && previousTimeLimitRef.current !== timeLimit;
    const isActiveChanged = previousIsActiveRef.current === false && isActive === true;
    
    // externalRemainingTimeが初めて渡された場合もリセット
    // （リロード時に後から渡されるケースに対応）
    const externalTimeInitialized = 
      initialExternalTimeRef.current === undefined && 
      externalRemainingTime !== undefined && 
      externalRemainingTime > 0;

    // 締切時刻が初めて渡された、または変わった場合もリセット
    // （リロード時に後から渡されるケース・問題切り替えに対応）
    const deadlineInitialized =
      previousDeadlineMsRef.current === undefined && deadlineMs !== undefined;
    const deadlineChanged =
      previousDeadlineMsRef.current !== undefined &&
      deadlineMs !== undefined &&
      previousDeadlineMsRef.current !== deadlineMs;

    if (timeLimitChanged || isActiveChanged || externalTimeInitialized || deadlineInitialized || deadlineChanged) {
      startTimeRef.current = null;
      timeoutFiredRef.current = false;
      clearTimer();
    }
    
    previousTimeLimitRef.current = timeLimit;
    previousIsActiveRef.current = isActive;
    previousDeadlineMsRef.current = deadlineMs;

    if (startTimeRef.current !== null) {
      return;
    }

    if (!isActive) {
      return;
    }

    timeoutFiredRef.current = false;

    if (deadlineMs !== undefined) {
      // 締切時刻モード: サーバー時刻基準の残り時間で開始する
      const remaining = Math.max(0, (deadlineMs - serverNow()) / 1000);
      if (remaining <= 0) {
        // 既に締切を過ぎている場合はタイマーを開始せず0秒で停止
        internalTimeRef.current = 0;
        startTimeRef.current = null;
        setDisplayTime(0);
        clearTimer();
      } else {
        // startTimeRef は tick 起動の判定用（残り時間計算には deadlineMs を使う）
        startTimeRef.current = Date.now();
        setDisplayTime(remaining);
        setTimerStartedKey(prev => prev + 1);
      }
    } else if (externalRemainingTime === undefined) {
      // externalRemainingTimeがundefinedの場合は、timeLimitから開始
      internalTimeRef.current = timeLimit;
      startTimeRef.current = Date.now();
      setDisplayTime(timeLimit);
      initialExternalTimeRef.current = undefined;
      setTimerStartedKey(prev => prev + 1);
    } else {
      // externalRemainingTimeが渡されている場合は、その値を使って開始時刻を逆算
      // externalRemainingTimeが0以下の場合はタイマーを再開始せず0秒で停止
      if (externalRemainingTime <= 0) {
        internalTimeRef.current = 0;
        startTimeRef.current = null;
        setDisplayTime(0);
        initialExternalTimeRef.current = 0;
        clearTimer();
      } else {
        initialExternalTimeRef.current = externalRemainingTime;
        startTimeRef.current = Date.now() - (timeLimit - externalRemainingTime) * 1000;
        setDisplayTime(externalRemainingTime);
        setTimerStartedKey(prev => prev + 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, timeLimit, externalRemainingTime, deadlineMs]);

  // Handle timer tick - 100msごとに更新して滑らかにする
  // onTimeout / onTick は ref 経由で呼ぶため依存に含めない
  useEffect(() => {
    if (timerStartedKey === 0 || startTimeRef.current === null) {
      return;
    }

    intervalRef.current = window.setInterval(() => {
      const now = Date.now();
      if (startTimeRef.current === null) return;

      // 締切時刻モードでは毎tickサーバー時刻基準の差分で計算する
      // （開始時アンカー起点の減算による端数切り捨て誤差を防ぐ）
      const newRemaining = deadlineMs !== undefined
        ? Math.max(0, (deadlineMs - serverNow()) / 1000)
        : Math.max(0, timeLimit - (now - startTimeRef.current) / 1000);

      if (deadlineMs === undefined && externalRemainingTime === undefined) {
        internalTimeRef.current = Math.floor(newRemaining);
      }

      setDisplayTime(newRemaining);

      if (deadlineMs !== undefined || externalRemainingTime !== undefined) {
        if (Math.floor(newRemaining) !== Math.floor(newRemaining + 0.1)) {
          onTickRef.current?.(Math.floor(newRemaining));
        }
      }

      if (newRemaining <= 0 && !timeoutFiredRef.current) {
        timeoutFiredRef.current = true;
        clearTimer();
        startTimeRef.current = null;
        initialExternalTimeRef.current = undefined;
        onTimeoutRef.current();
      }
    }, 100);

    return () => {
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerStartedKey, timeLimit, deadlineMs]); // clearTimer, onTimeout, onTick を依存から除外

  // Check for timeout when using deadline / external remaining time
  useEffect(() => {
    const deadlinePassed = deadlineMs !== undefined && deadlineMs - serverNow() <= 0;
    const externalExpired = externalRemainingTime !== undefined && externalRemainingTime <= 0;
    if (isActive && (deadlinePassed || externalExpired) && !timeoutFiredRef.current) {
      timeoutFiredRef.current = true;
      onTimeoutRef.current();
    }
  }, [isActive, externalRemainingTime, deadlineMs]); // onTimeout を依存から除外

  const currentRemaining = displayTime;
  const percentage = Math.max(0, Math.min(100, (currentRemaining / timeLimit) * 100));
  // バーは経過時間を表し、左から右へ増えていく表示にする
  const elapsedPercentage = 100 - percentage;

  let color = '#4CAF50';
  if (percentage <= 20) color = '#F44336';
  else if (percentage <= 50) color = '#FF9800';

  return (
    <div className="test-timer-full" role="timer" aria-label={`残り時間: ${formatTime(Math.ceil(currentRemaining))}秒`} aria-live="polite" aria-atomic="true">
      <div className="timer-display" style={{ color }}>
        <span className="timer-icon">
          <svg className="hi-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M9 1.5A.75.75 0 0 1 9.75.75h4.5a.75.75 0 0 1 0 1.5h-4.5A.75.75 0 0 1 9 1.5ZM12 6a9 9 0 1 0 9 9 9.01 9.01 0 0 0-9-9Zm0 1.5a7.5 7.5 0 1 0 7.5 7.5A7.509 7.509 0 0 0 12 7.5Zm.75 3a.75.75 0 0 0-1.5 0v4.19l2.28 2.28a.75.75 0 1 0 1.06-1.06l-1.84-1.84V10.5Z" clipRule="evenodd"/>
          </svg>
        </span>
        {/* 管理画面（AnsweringCountdown）と同じ切り上げ表示に統一 */}
        <span id="timer-display" style={{ fontSize: '13px', fontWeight: 600 }}>
          {Math.ceil(currentRemaining)}秒
        </span>
      </div>
      <div className="timer-seekbar" id="timer-seekbar-container">
        <div className="timer-seekbar-bg">
          <div
            className="timer-seekbar-fill"
            id="timer-seekbar-fill"
            style={{
              width: `${elapsedPercentage}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    </div>
  );
}
